import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RestrictionService } from '@core/services/restriction.service';
import { RestrictionView } from '@core/models/viewmodel/restriction.view';
import {
  CoverageReason,
  CoverageSummaryResponse,
  ExecutionEnforcementResponse,
  PupilEnforcementResponse
} from '@core/models/response/restriction.response';
import {
  COVERAGE_REASON_LABELS,
  EnforcementBucket,
  CoverageReasonEntry,
  coverageDropped,
  coverageHeadline,
  coverageOf,
  coveragePercentage,
  coverageReasons,
  coverageTone,
  percentageLabel,
  pupilCoverageLabel,
  pupilEnforcementClass,
  pupilEnforcementLabel,
  pupilsForReason,
  pupilsInBucket,
  pupilsMatchingSearch,
  pupilsNeedingAttention
} from '@core/utils/restriction-coverage.util';

export interface ExecutionEnforcementDialogData {
  task: RestrictionView;
}

/**
 * Everything the phones said about one restriction (DIGI-1724, DIGI-1699), on its own screen.
 *
 * <p>Coverage first, because it is the question that comes first: a phone with no Screen Time
 * access was never going to confirm a shield, and counting it as "not confirmed" hides a problem
 * a family can actually fix. Then the three enforcement counts, then every pupil. "Not confirmed"
 * is never presented as "not shielded": below iOS 16 a phone that is shielding correctly cannot
 * say so either way.</p>
 */
@Component({
  selector: 'app-execution-enforcement-dialog',
  templateUrl: './execution-enforcement-dialog.component.html',
  styleUrls: ['./execution-enforcement-dialog.component.scss', './restriction-coverage.scss'],
  standalone: false
})
export class ExecutionEnforcementDialogComponent implements OnInit {
  enforcement?: ExecutionEnforcementResponse;
  loading = false;
  /** True once a check came back empty: the screen says so rather than showing zeroes. */
  unavailable = false;
  activeReason?: CoverageReason;
  /**
   * A year group can run to hundreds of pupils, so the list opens on the ones to chase and the
   * confirmed ones are one click away.
   */
  showConfirmed = false;
  search = '';
  /** The count chip being chased, when one was clicked. */
  activeBucket?: EnforcementBucket;

  /** What each of the three counts means, on hover. */
  readonly bucketHints: Record<EnforcementBucket, string> = {
    SHIELDED: 'The phone confirmed the restriction was in place.',
    NOT_CONFIRMED: 'The phone never confirmed. It does not mean the pupil went unrestricted: before iOS 16 a phone that is shielding correctly cannot confirm it.',
    NO_DEVICE: 'No phone answered for this pupil, so nothing could be held.'
  };

  private readonly bucketLabels: Record<EnforcementBucket, string> = {
    SHIELDED: 'Confirmed shielded',
    NOT_CONFIRMED: 'Not confirmed',
    NO_DEVICE: 'No device'
  };

  constructor(
    private restrictionService: RestrictionService,
    private dialogRef: MatDialogRef<ExecutionEnforcementDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExecutionEnforcementDialogData
  ) {}

  ngOnInit(): void {
    this.load();
  }

  get task(): RestrictionView {
    return this.data.task;
  }

  /** Asks again: the counts move as phones confirm while a restriction is running. */
  load(): void {
    const executionId = this.task?.taskExecutionId;
    if (!executionId || this.loading) {
      return;
    }
    this.loading = true;
    this.restrictionService.loadExecutionEnforcement(executionId).subscribe({
      next: (result) => {
        this.loading = false;
        this.enforcement = result ?? undefined;
        this.unavailable = !result;
      },
      // A screen that cannot say what the phones did must say so, never show a zero.
      error: () => {
        this.loading = false;
        this.enforcement = undefined;
        this.unavailable = true;
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  /** The reason chips double as the filter: staff read a count and want the names behind it. */
  toggleReason(reason: CoverageReason): void {
    this.activeReason = this.activeReason === reason ? undefined : reason;
    this.activeBucket = undefined;
  }

  /** The three counts are filters too: a staff member reads a number and wants the names. */
  toggleBucket(bucket: EnforcementBucket): void {
    this.activeBucket = this.activeBucket === bucket ? undefined : bucket;
    this.activeReason = undefined;
  }

  isBucketActive(bucket: EnforcementBucket): boolean {
    return this.activeBucket === bucket;
  }

  /** Back to the default view, whichever filter was on. */
  clearFilters(): void {
    this.activeReason = undefined;
    this.activeBucket = undefined;
  }


  clearReason(): void {
    this.activeReason = undefined;
  }

  get isFiltered(): boolean {
    return !!this.activeReason || !!this.activeBucket;
  }

  isReasonActive(reason: CoverageReason): boolean {
    return this.activeReason === reason;
  }

  get activeReasonLabel(): string {
    return this.activeReason ? COVERAGE_REASON_LABELS[this.activeReason] : '';
  }

  get coverage(): CoverageSummaryResponse | null {
    return coverageOf(this.enforcement);
  }

  /** Every pupil the restriction asked something of. */
  get allPupils(): Array<PupilEnforcementResponse> {
    return this.enforcement?.pupils ?? [];
  }

  /** The list on screen: a chased reason, or the pupils to chase, then the name search. */
  get pupils(): Array<PupilEnforcementResponse> {
    return pupilsMatchingSearch(this.filteredPupils, this.search);
  }

  private get filteredPupils(): Array<PupilEnforcementResponse> {
    if (this.activeReason) {
      return pupilsForReason(this.allPupils, this.activeReason);
    }
    if (this.activeBucket) {
      return pupilsInBucket(this.allPupils, this.activeBucket);
    }
    return this.showConfirmed ? this.allPupils : pupilsNeedingAttention(this.allPupils);
  }

  get attentionCount(): number {
    return pupilsNeedingAttention(this.allPupils).length;
  }

  get confirmedCount(): number {
    return this.allPupils.length - this.attentionCount;
  }

  /** The heading above the list, so it is never unclear which pupils are being shown. */
  get listHeading(): string {
    if (this.activeReason) {
      return this.activeReasonLabel;
    }
    if (this.activeBucket) {
      return `${this.bucketLabels[this.activeBucket]} (${this.filteredPupils.length})`;
    }
    return this.showConfirmed ? `All pupils (${this.allPupils.length})` : `To chase (${this.attentionCount})`;
  }

  /** Nothing to chase reads as good news, not as an empty list. */
  get emptyListMessage(): string {
    if (this.search) {
      return 'No pupil matches that name.';
    }
    if (!this.isFiltered && !this.showConfirmed && this.allPupils.length > 0) {
      return 'No pupils to chase. Every phone confirmed this restriction.';
    }
    return 'No pupils to show.';
  }

  toggleConfirmed(): void {
    this.showConfirmed = !this.showConfirmed;
  }

  onSearch(value: string): void {
    this.search = value;
  }

  coverageHeadline(coverage: CoverageSummaryResponse): string {
    return coverageHeadline(coverage);
  }

  coveragePercentageLabel(coverage: CoverageSummaryResponse): string {
    return percentageLabel(coveragePercentage(coverage));
  }

  coveragePercentage(coverage: CoverageSummaryResponse): number {
    return coveragePercentage(coverage);
  }

  coverageTone(coverage: CoverageSummaryResponse): string {
    return coverageTone(coverage);
  }

  coverageReasons(coverage: CoverageSummaryResponse): Array<CoverageReasonEntry> {
    return coverageReasons(coverage);
  }

  coverageDropped(coverage: CoverageSummaryResponse): number | null {
    return coverageDropped(coverage);
  }

  trackByReason(_index: number, entry: CoverageReasonEntry): CoverageReason {
    return entry.reason;
  }

  trackByPupil(_index: number, pupil: PupilEnforcementResponse): string {
    return pupil.childAccountId;
  }

  pupilLabel(pupil: PupilEnforcementResponse): string {
    return pupilEnforcementLabel(pupil);
  }

  pupilClass(pupil: PupilEnforcementResponse): string {
    return pupilEnforcementClass(pupil);
  }

  pupilReason(pupil: PupilEnforcementResponse): string {
    return pupilCoverageLabel(pupil);
  }
}
